import { useQueryClient } from '@tanstack/react-query';
import {
  useListNotificationsV1NotificationsGet,
  getListNotificationsV1NotificationsGetQueryKey,
  useUpdateNotificationStatusV1NotificationsNotificationIdPatch,
} from '@/api/generated-catch-backend/notifications/notifications';
import type { NotificationWithLinksResponse } from '@/api/generated-catch-backend/model';

const CRITICAL_PRIORITY = 10000;

/**
 * Hook to fetch and manage critical lab result notifications
 *
 * Fetches unread critical lab notifications and provides a function to dismiss them.
 * Automatically invalidates the query cache after dismissal.
 */
export function useCriticalLabNotifications() {
  const queryClient = useQueryClient();

  // Fetch unread critical lab notifications
  const {
    data,
    isLoading,
    error,
  } = useListNotificationsV1NotificationsGet({
    status: ['unread'],
    type: ['lab_result'],
    priority: [CRITICAL_PRIORITY],
  });

  // Mutation to dismiss a notification
  const { mutateAsync: updateStatus, isPending: isDismissing } =
    useUpdateNotificationStatusV1NotificationsNotificationIdPatch({
      mutation: {
        onSuccess: () => {
          // Invalidate the notifications query to refetch and update UI
          queryClient.invalidateQueries({
            queryKey: getListNotificationsV1NotificationsGetQueryKey({
              status: ['unread'],
              type: ['lab_result'],
              priority: [CRITICAL_PRIORITY],
            }),
          });
        },
      },
    });

  /**
   * Dismiss a notification by marking it as dismissed
   */
  const dismissNotification = async (notificationId: string) => {
    await updateStatus({
      notificationId,
      data: { status: 'dismissed' },
    });
  };

  const notifications: NotificationWithLinksResponse[] = data?.notifications || [];

  return {
    notifications,
    isLoading,
    error,
    dismissNotification,
    isDismissing,
  };
}
